import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Clock, Trophy, AlertTriangle, Hourglass, Check, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const STATUS_BADGE = {
  proposed: { label: "Proposed", cls: "bg-amber-300/15 text-amber-300 border-amber-300/30", Icon: Hourglass },
  accepted: { label: "Accepted", cls: "bg-[#C6FF00]/15 text-[#C6FF00] border-[#C6FF00]/40", Icon: Check },
  declined: { label: "Declined", cls: "bg-red-500/15 text-red-400 border-red-500/30", Icon: X },
  in_progress: { label: "In Progress", cls: "bg-white/10 text-white border-white/20 animate-pulse", Icon: Clock },
  completed: { label: "Completed", cls: "bg-neutral-700/30 text-neutral-200 border-white/10", Icon: Trophy },
  cancelled: { label: "Cancelled", cls: "bg-red-500/10 text-red-400/80 border-red-500/20", Icon: X },
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

const Schedule = () => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/set-tests");
      setTests(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const buckets = {
    upcoming: tests.filter((t) => ["proposed", "accepted", "in_progress"].includes(t.status)),
    completed: tests.filter((t) => t.status === "completed"),
    other: tests.filter((t) => ["declined", "cancelled"].includes(t.status)),
  };

  return (
    <div data-testid="schedule-page">
      <p className="text-xs uppercase tracking-[0.3em] text-[#C6FF00] font-semibold mb-1">
        Set Tests
      </p>
      <h1 className="font-heading text-4xl md:text-5xl mb-8">Show Up.</h1>

      {loading ? (
        <div className="space-y-4">
          {[0, 1].map((i) => (
            <div key={i} className="h-32 bg-[#111] rounded-2xl border border-white/5 animate-pulse" />
          ))}
        </div>
      ) : tests.length === 0 ? (
        <div className="text-center py-20" data-testid="schedule-empty">
          <Calendar className="text-[#C6FF00] mx-auto mb-4" size={36} />
          <h2 className="font-heading text-3xl mb-2">No Set Tests yet.</h2>
          <p className="text-sm text-neutral-400 mb-6">
            Open a chat with a match and propose your first Set Test.
          </p>
          <Link
            to="/app/matches"
            className="inline-block bg-[#C6FF00] text-[#0A0A0A] font-bold text-xs uppercase tracking-[0.25em] px-6 py-3 rounded-md"
          >
            See Matches
          </Link>
        </div>
      ) : (
        <div className="space-y-10" data-testid="schedule-list">
          {buckets.upcoming.length > 0 && (
            <Section title="Upcoming" tests={buckets.upcoming} />
          )}
          {buckets.completed.length > 0 && (
            <Section title="Completed" tests={buckets.completed} dim />
          )}
          {buckets.other.length > 0 && (
            <Section title="Other" tests={buckets.other} dim />
          )}
        </div>
      )}
    </div>
  );
};

const Section = ({ title, tests, dim }) => (
  <div>
    <p className="text-xs uppercase tracking-[0.25em] text-neutral-500 font-semibold mb-4">
      {title}
    </p>
    <div className="space-y-3">
      {tests.map((t) => (
        <SetTestRow key={t.id} t={t} dim={dim} />
      ))}
    </div>
  </div>
);

const SetTestRow = ({ t, dim }) => {
  const meta = STATUS_BADGE[t.status] || STATUS_BADGE.proposed;
  const Icon = meta.Icon;
  const lockedCount = (t.sets || []).filter((s) => s.locked).length;
  const planCount = (t.plan || []).length;

  return (
    <Link
      to={`/app/set-test/${t.id}`}
      data-testid={`set-test-row-${t.id}`}
      className={`block rounded-2xl border transition-colors ${
        dim
          ? "bg-[#0F0F0F] border-white/5 opacity-80 hover:opacity-100"
          : "bg-[#111] border-white/5 hover:border-[#C6FF00]/40"
      }`}
    >
      <div className="flex items-center gap-4 p-4">
        <div className="flex flex-col items-center justify-center w-16 h-16 rounded-xl bg-[#C6FF00]/10 border border-[#C6FF00]/30 text-[#C6FF00]">
          <span className="font-heading text-2xl leading-none">
            {new Date(t.scheduled_at).getDate()}
          </span>
          <span className="text-[9px] uppercase tracking-widest mt-0.5">
            {new Date(t.scheduled_at).toLocaleString(undefined, { month: "short" })}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading text-2xl text-white leading-none">{t.title}</h3>
            <span
              className={`px-2 py-0.5 rounded-full border text-[9px] font-bold uppercase tracking-[0.15em] flex items-center gap-1 ${meta.cls}`}
            >
              <Icon size={10} /> {meta.label}
            </span>
          </div>
          <p className="text-xs text-neutral-300 mt-1">
            with{" "}
            <span className="text-[#C6FF00] font-semibold">
              {t.other_user?.name || "Match"}
            </span>
          </p>
          <div className="flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-widest text-neutral-500 mt-2">
            <span className="flex items-center gap-1">
              <Clock size={10} /> {fmtDate(t.scheduled_at)} · {fmtTime(t.scheduled_at)}
            </span>
            {t.location && (
              <span className="flex items-center gap-1">
                <MapPin size={10} /> {t.location}
              </span>
            )}
            <span>
              {lockedCount}/{planCount} sets
            </span>
          </div>
        </div>
        {t.status === "completed" && (
          <span
            className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
              t.pass_fail === "pass"
                ? "bg-[#C6FF00] text-[#0A0A0A]"
                : "bg-red-500/15 text-red-400 border border-red-500/30"
            }`}
          >
            {t.pass_fail === "pass" ? "Pass" : "Fail"}
          </span>
        )}
      </div>
    </Link>
  );
};

export default Schedule;
